import { z } from 'zod';

export const ACADEMIC_YEAR_STATUSES = ['draft', 'current', 'closed', 'archived'] as const;

export const AcademicYearSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  status: z.enum(ACADEMIC_YEAR_STATUSES),
});
export type AcademicYear = z.infer<typeof AcademicYearSchema>;

export const StudentSearchHitSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  matricule: z.string(),
  cin: z.string().nullable(),
  /** Current-year level code and group, e.g. GL3 / A. */
  levelCode: z.string().nullable(),
  groupName: z.string().nullable(),
});
export type StudentSearchHit = z.infer<typeof StudentSearchHitSchema>;

export const SearchQuerySchema = z.object({ q: z.string().trim().max(100).default('') });
export const SearchResultSchema = z.object({ students: z.array(StudentSearchHitSchema) });
export type SearchResult = z.infer<typeof SearchResultSchema>;

/**
 * In-app notifications are stored as a key + parameters and translated by the client in the reader's
 * language (codes such as a weekday or a document kind are translated there too).
 */
export const NOTIFICATION_KEYS = [
  'absence.recorded',
  'absence.alert',
  'timetable.room',
  'timetable.conflict',
  'justification.approved',
  'justification.rejected',
  'request.done',
  'request.rejected',
  'deliberation.pvToValidate',
  'gradeReview.requested',
  'gradeReview.answered',
  'internship.decision',
  'internship.supervisionRequested',
  'internship.supervisionAnswered',
  'internship.defensePublished',
  'timetable.teacherAbsent',
] as const;
export type NotificationKey = (typeof NOTIFICATION_KEYS)[number];
export const NotificationParamsSchema = z.record(z.string(), z.union([z.string(), z.number(), z.null()]));
export type NotificationParams = z.infer<typeof NotificationParamsSchema>;

export const NotificationSummarySchema = z.object({
  unread: z.number().int(),
  items: z.array(
    z.object({
      id: z.uuid(),
      kind: z.string(),
      /** Translation key (`notifications.<key>.title|body`); title/body are the French text for older rows. */
      key: z.enum(NOTIFICATION_KEYS).nullable(),
      params: NotificationParamsSchema.nullable(),
      title: z.string(),
      body: z.string(),
      link: z.string().nullable(),
      createdAt: z.string(),
      read: z.boolean(),
    }),
  ),
});
export type NotificationSummary = z.infer<typeof NotificationSummarySchema>;
